import { type ChatbotMessage, MessageType, ResponseStage, type TurnPending } from '@stores/chatbot/types';

export type { TurnPending };

/**
 * The rule behind the chat-completion chime, kept free of React and of the
 * chatbot store so it can be exercised as the small state machine it is.
 * `useChatTurnChime` is the thin wiring that feeds it real snapshots.
 *
 * A turn has two different "done" moments, and the gap between them is
 * routinely fifteen seconds:
 *
 *   - `answerCompletedAt` is stamped on the server's `graph_complete` marker,
 *     when the answer stops arriving on screen. This is what a person means
 *     by done, and it is what the chime fires on.
 *   - the root `responseStage` goes IDLE only after the post-turn phase
 *     (grooming, post-turn tools) finishes, long after anyone stopped reading.
 *     It delimits one turn from the next, and is the backstop for end paths
 *     that never emit `graph_complete` — structured questions, an interrupted
 *     stream, an error.
 *
 * Leo is not done at `graph_complete` when the turn left work that comes back
 * into the chat: a read that outran its deadline, a code job, a module
 * operation. `graph_complete` then carries `pending`, and the real answer is a
 * woken turn (`continuation_<id>`) minutes later. Nor is Leo done when the
 * person sent messages mid-turn that the turn did not read: they go on as the
 * next turn. Both are one ask, announced once, when it is really answered.
 */

// Tuned by feel, not by measurement — move it if two minutes turns out to be
// the wrong side of "did I walk away?".
export const CHIME_AFTER_MS = 2 * 60 * 1000;
// The server's continuation window (parent_continuation.MAX_AGE_SECONDS): a
// wake not back by then never comes.
export const HOLD_MAX_MS = 24 * 60 * 60 * 1000;

/** `graph_complete.pending` as the stream delivered it; null unless something is coming back. */
export function readTurnPending(raw: unknown): TurnPending | null {
  if (!raw || typeof raw !== 'object' || (raw as TurnPending).continuation !== true) return null;
  const { reads, jobs, continuations } = raw as TurnPending;
  return {
    continuation: true,
    reads: typeof reads === 'number' ? reads : 0,
    jobs: typeof jobs === 'number' ? jobs : 0,
    continuations: Array.isArray(continuations)
      ? continuations.filter((id): id is string => typeof id === 'string')
      : [],
  };
}

/**
 * The turn id of the reply the latest turn wrote, when it has one: a woken
 * turn's reply carries `continuation_<id>`. Only replies after the person's
 * last message count.
 */
export function latestReplyTurnId(messages: readonly ChatbotMessage[]): string | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.hidden) continue;
    if (message.type === MessageType.USER && !message.harness) return undefined;
    if (message.type === MessageType.ASSISTANT) return message.turnId;
  }
  return undefined;
}

const CONTINUATION_PREFIX = 'continuation_';

export interface TurnSnapshot {
  /** The store's root `responseStage` — settles only after post-turn work. */
  rootStage: ResponseStage;
  /** Stamped when the answer itself finished arriving; null before the first. */
  answerCompletedAt: number | null;
  conversationId: string | null | undefined;
  /** The latest `graph_complete`'s `pending`, set alongside `answerCompletedAt`. */
  answerPending?: TurnPending | null;
  /** Messages sent mid-turn that no turn has read yet: they go on as the next turn. */
  inboxWaiting?: boolean;
  /** `latestReplyTurnId` of the conversation's messages. */
  replyTurnId?: string;
}

export type TurnChimeResult =
  /** A turn worth announcing ended. */
  | { kind: 'chime' }
  /** The turn ended but left work coming back: hold the announcement for it (`createHandoffHolds`). */
  | { kind: 'handoff'; conversationId: string; startedAt: number; continuationIds: string[] }
  /** A woken turn ended: one of the wakes a held turn waits on is back. */
  | { kind: 'continuation'; conversationId: string; continuationId: string; ok: boolean }
  | null;

export type TurnChimeTracker = (snapshot: TurnSnapshot, now: number) => TurnChimeResult;

/**
 * Feed it every store snapshot; it answers whether this one ended a turn worth
 * announcing, or a turn whose announcement waits on work still coming back. It
 * keeps the previous root stage itself, so callers don't have to reason about
 * which of the store's many settle paths they're looking at.
 *
 * Phases: `idle` (no turn watched), `running` (a turn this tracker saw begin),
 * `chained` (the answer landed, but messages the turn did not read go on as the
 * next turn — which continues the same ask, timed from its first turn).
 */
export function createTurnChimeTracker(
  thresholdMs: number = CHIME_AFTER_MS,
): TurnChimeTracker {
  let previousRootStage = ResponseStage.IDLE;
  let phase: 'idle' | 'running' | 'chained' = 'idle';
  // When the ask began: the first turn of a chain, not the one now running.
  let startedAt: number | null = null;
  let startedConversationId: string | null = null;
  // The completion stamp as it stood when this turn began. Comparing against
  // it is what makes a NEW completion detectable without the store having to
  // reset the field, so there is no ordering race at the start of a turn.
  let answerCompletedAtOnStart: number | null = null;
  let errored = false;
  // What the turn that started a chain left pending, for a chain that ends
  // without its next turn (what was waiting went back to the composer).
  let chainPending: TurnPending | null = null;

  // A turn over: what, if anything, to announce. Returning to `idle` first is
  // what makes each turn end exactly once, although the post-turn phase keeps
  // pushing state changes after the answer is done.
  const settle = (
    snapshot: TurnSnapshot,
    pending: TurnPending | null | undefined,
    answered: boolean,
    now: number,
  ): TurnChimeResult => {
    phase = 'idle';
    const conversationId = snapshot.conversationId ?? null;
    // Switching conversation mid-stream discards the result.
    if (startedAt === null || conversationId === null || conversationId !== startedConversationId) return null;
    const replyTurnId = snapshot.replyTurnId;
    if (replyTurnId?.startsWith(CONTINUATION_PREFIX)) {
      // A woken turn. Only its own graph_complete says it came back; a tail that
      // dropped leaves the answer to the feed's completion event.
      if (!answered) return null;
      return {
        kind: 'continuation',
        conversationId,
        continuationId: replyTurnId.slice(CONTINUATION_PREFIX.length),
        ok: !errored,
      };
    }
    // A failed turn is not an answer worth announcing.
    if (errored) return null;
    if (pending?.continuation) {
      return { kind: 'handoff', conversationId, startedAt, continuationIds: pending.continuations ?? [] };
    }
    return now - startedAt >= thresholdMs ? { kind: 'chime' } : null;
  };

  return function onSnapshot(snapshot, now) {
    const { rootStage, answerCompletedAt } = snapshot;
    const conversationId = snapshot.conversationId ?? null;

    // Leaving IDLE is a turn starting. Mounting mid-turn leaves the phase
    // `idle`, so a turn we never saw begin is never announced.
    if (previousRootStage === ResponseStage.IDLE && rootStage !== ResponseStage.IDLE) {
      if (phase !== 'chained' || conversationId !== startedConversationId) {
        startedAt = now;
        startedConversationId = conversationId;
      }
      answerCompletedAtOnStart = answerCompletedAt;
      errored = false;
      phase = 'running';
    }
    previousRootStage = rootStage;

    if (rootStage === ResponseStage.ERROR) errored = true;

    let result: TurnChimeResult = null;
    if (phase === 'running') {
      const answered = answerCompletedAt !== answerCompletedAtOnStart;
      if (answered && snapshot.inboxWaiting && !errored) {
        // What the person sent mid-turn goes on as the next message.
        phase = 'chained';
        chainPending = snapshot.answerPending ?? null;
      } else if (answered || rootStage === ResponseStage.IDLE) {
        result = settle(snapshot, answered ? snapshot.answerPending : null, answered, now);
      }
    } else if (phase === 'chained' && rootStage === ResponseStage.IDLE && !snapshot.inboxWaiting) {
      // Nothing went on after all (it went back to the composer): the chain
      // ends at the answer it already has.
      result = settle(snapshot, chainPending, true, now);
    }

    // The root settling is the turn truly over — only then is the clock clear
    // for the next one. A chain keeps it for the turn that continues it.
    if (rootStage === ResponseStage.IDLE && phase !== 'chained') {
      startedAt = null;
      startedConversationId = null;
      errored = false;
      chainPending = null;
    }

    return result;
  };
}

/** A turn whose announcement waits for the wakes it left to come back. */
export interface HeldTurn {
  conversationId: string;
  /** When the ask began: the threshold is measured from here, not from the wake. */
  startedAt: number;
  heldAt: number;
  /** Continuation ids still to come back. Empty: the next one for this chat settles it. */
  waiting: string[];
  /** Whether any wake came back with an answer. */
  answered: boolean;
}

export type HeldTurnEnd = 'chime' | 'stopped' | null;

export interface HandoffHolds {
  hold(conversationId: string, startedAt: number, continuationIds: string[], now: number): void;
  /**
   * A wake came back (`ok`) or ended without an answer. Once a held turn has
   * nothing left to wait on it is announced — `chime` when any wake answered,
   * `stopped` when none did — if the ask ran past the threshold.
   */
  finish(conversationId: string, continuationId: string, ok: boolean, now: number): HeldTurnEnd;
  held(): HeldTurn[];
}

/**
 * The turns waiting on their wakes, across every chat. Kept outside any one
 * conversation's tracker because a wake comes back whichever chat is on screen,
 * after a reload, or with the chat closed; `useChatTurnChime` persists `held()`
 * for the tab and restores it here.
 */
export function createHandoffHolds(
  thresholdMs: number = CHIME_AFTER_MS,
  restored: readonly HeldTurn[] = [],
  now: number = Date.now(),
): HandoffHolds {
  let holds: HeldTurn[] = restored
    .filter(turn => turn && typeof turn.conversationId === 'string' && Array.isArray(turn.waiting))
    .filter(turn => now - turn.heldAt < HOLD_MAX_MS)
    .map(turn => ({ ...turn, waiting: [...turn.waiting] }));

  return {
    hold(conversationId, startedAt, continuationIds, at) {
      holds.push({ conversationId, startedAt, heldAt: at, waiting: [...new Set(continuationIds)], answered: false });
    },

    finish(conversationId, continuationId, ok, at) {
      let end: HeldTurnEnd = null;
      const still: HeldTurn[] = [];
      for (const turn of holds) {
        const matches =
          turn.conversationId === conversationId &&
          (turn.waiting.length === 0 || turn.waiting.includes(continuationId));
        if (!matches) {
          still.push(turn);
          continue;
        }
        const waiting = turn.waiting.filter(id => id !== continuationId);
        const answered = turn.answered || ok;
        if (waiting.length > 0) {
          still.push({ ...turn, waiting, answered });
          continue;
        }
        // Two held turns can share one wake (the second asked again for work in
        // flight); the person hears once, and a chime outranks a stop.
        if (at - turn.startedAt >= thresholdMs && end !== 'chime') end = answered ? 'chime' : 'stopped';
      }
      holds = still;
      return end;
    },

    held() {
      return holds.map(turn => ({ ...turn, waiting: [...turn.waiting] }));
    },
  };
}
